import { useEffect, useState } from 'react';
import { api } from '../../lib/api';

interface EmailTemplate {
  id: string;
  slug: string;
  subject: string;
  body_html: string;
  variables: string[];
}

export default function EmailTemplates() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selected, setSelected] = useState<EmailTemplate | null>(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => { fetchTemplates(); }, []);

  async function fetchTemplates() {
    setLoading(true);
    try {
      const data = await api.get<{ templates: EmailTemplate[] }>('/email/templates');
      setTemplates(data.templates ?? []);
    } catch (e: any) {
      showMsg('error', e.message);
    } finally {
      setLoading(false);
    }
  }

  function selectTemplate(tpl: EmailTemplate) {
    setSelected(tpl);
    setEditSubject(tpl.subject);
    setEditBody(tpl.body_html);
    setMessage(null);
  }

  function showMsg(type: 'success' | 'error', text: string) {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  }

  function getPreview(): string {
    return editBody.replace(/\{\{(\w+)\}\}/g, (_, key) => `<mark>[${key}]</mark>`);
  }

  async function handleSave() {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/email/templates/${selected.id}`, { subject: editSubject, body_html: editBody });
      await fetchTemplates();
      showMsg('success', 'Template opgeslagen');
    } catch (e: any) {
      showMsg('error', e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleTestSend() {
    if (!selected || !testEmail) return;
    setTestSending(true);
    try {
      await api.post('/email/test', {
        template_slug: selected.slug,
        recipient_email: testEmail,
        variables: {},
      });
      showMsg('success', `Test e-mail verstuurd naar ${testEmail}`);
    } catch (e: any) {
      showMsg('error', e.message);
    } finally {
      setTestSending(false);
    }
  }

  if (loading) return <div className="p-8 text-center text-gray-500">Templates laden...</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">E-mail Templates</h1>

      {message && (
        <div className={`p-3 rounded text-sm ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {message.text}
        </div>
      )}

      <div className="flex gap-6">
        {/* Template list */}
        <div className="w-64 flex-shrink-0">
          <div className="card divide-y divide-gray-200">
            {templates.map(tpl => (
              <button
                key={tpl.id}
                className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 ${selected?.id === tpl.id ? 'bg-indigo-50 text-indigo-700 font-medium' : ''}`}
                onClick={() => selectTemplate(tpl)}
              >
                <div className="font-medium">{tpl.slug}</div>
                <div className="text-xs text-gray-400 truncate">{tpl.subject}</div>
              </button>
            ))}
            {templates.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-gray-400">Geen templates</div>
            )}
          </div>
        </div>

        {/* Editor */}
        {selected ? (
          <div className="flex-1 min-w-0 space-y-4">
            <div>
              <label className="label">Slug</label>
              <input className="input bg-gray-50 text-gray-500" value={selected.slug} disabled />
            </div>

            <div>
              <label className="label">Onderwerp</label>
              <input className="input" value={editSubject} onChange={e => setEditSubject(e.target.value)} />
            </div>

            {selected.variables?.length > 0 && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                <span className="font-medium">Beschikbare variabelen: </span>
                {selected.variables.map(v => (
                  <code key={v} className="mx-1 px-1.5 py-0.5 bg-yellow-100 rounded text-xs">{`{{${v}}}`}</code>
                ))}
              </div>
            )}

            <div>
              <label className="label">Body (HTML)</label>
              <textarea
                className="input font-mono"
                rows={14}
                value={editBody}
                onChange={e => setEditBody(e.target.value)}
              />
            </div>

            <div>
              <label className="label">Preview</label>
              <div className="card p-4 text-sm min-h-[80px]" dangerouslySetInnerHTML={{ __html: getPreview() }} />
            </div>

            <div className="flex items-center gap-3">
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Opslaan...' : 'Opslaan'}
              </button>
              <div className="flex items-center gap-2">
                <input
                  type="email"
                  placeholder="test@example.com"
                  className="input w-56"
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                />
                <button onClick={handleTestSend} disabled={testSending || !testEmail} className="btn-secondary">
                  {testSending ? 'Versturen...' : 'Test versturen'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
            Selecteer een template om te bewerken
          </div>
        )}
      </div>
    </div>
  );
}
